import React, { useState, useEffect, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Autocomplete,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Warning as WarningIcon,
  LocalPharmacy as LocalPharmacyIcon,
  Print as PrintIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { medicationService, Medication } from '../../services/medicationService';
import { skipWhenOffline } from '../../offline';
import DeductFromLocationSelect from '../Inventory/DeductFromLocationSelect';
import DeaBottleSelect from '../DEALogs/DeaBottleSelect';
import QuantityField from '../QuantityField';
import { buildLabelData, RxLabelSampleData } from './RxLabel';
import { printRxLabels } from './printLabel';
import {
  datePickerToCalendarValue,
  todayLocalISO,
  ymdToNoonUtcIso,
} from '../../utils/dateUtils';

interface JoinedMed {
  trade_name: string;
  active_ingredients: string[];
  sponsor_name?: string;
  application_number?: string;
}

interface CatalogItem {
  name: string;
  basePrice: number;
}

interface MedicationRow {
  name: string;
  dosage: string;
  quantity: number;
  instructions: string;
  startDate: Date | null;
  endDate: Date | null;
  // Per-unit price, captured when the drug is picked from a priced catalog
  // item. Sent through as Medication.unitPrice so the auto-bill line item
  // isn't silently $0. undefined for free-typed / unpriced drugs.
  unitPrice?: number;
  // ─── Rx refill fields ─────────────────────────────────────────────────────
  // isRx=true marks this medication as a take-home prescription that can be
  // refilled, dispensed via the Refill button, and printed as a label. When
  // false it's a legacy in-clinic medication record (no refill tracking).
  isRx: boolean;
  // Number of refills allowed BEYOND the initial fill. 0 = initial fill only.
  // DEA Schedule II is hard-blocked to 0 on the backend.
  refillsAuthorized: number;
  // Defaults to startDate + 12 months on the backend when omitted and isRx
  // is true. Vet may override per-Rx via the date picker.
  expirationDate: Date | null;
  // How many physical labels to print for this Rx on save. 0 = record the Rx
  // but don't dispense the initial fill or print anything now (the vet can
  // dispense later via the Refill button). 1–5 = dispense the initial fill
  // (which records the fill, decrements inventory, and bills the visit) and
  // print that many copies of the label.
  labelCount: number;
  // DEA controlled-substance schedule ("II"–"V"), or "" when not controlled.
  // Schedule II cannot be refilled — picking it forces refillsAuthorized to 0
  // and disables the refills picker (the backend hard-blocks it too).
  deaSchedule: string;
  // Stock location the dispense deducts from. Stamped from the dialog-level
  // "Deduct stock from" select onto every saved row; undefined = Automatic
  // (server infers: vet's truck if assigned, else warehouse — ADR-0013).
  dispenseLocationId?: string;
  // Bottle (ControlledSubstanceLot.id) for the DEA auto-log when the name
  // matches the clinic's CS registry; undefined = server FIFO auto-pick
  // (ADR-0014). Per-row — each row can be a different drug.
  deaLotId?: string;
}

/**
 * What `onSave` receives: the row with its three CALENDAR pickers already
 * anchored as noon-UTC ISO strings, plus the job-level deduction location.
 */
export type MedicationRowPayload = Omit<
  MedicationRow,
  'startDate' | 'endDate' | 'expirationDate'
> & {
  startDate: string;
  endDate?: string;
  expirationDate?: string;
};

/**
 * Anchor one row's CALENDAR pickers before it is posted — the W10 fix
 * (DATE-KINDS.md §2.5).
 *
 * A MUI `<DatePicker>` hands back a `Date` at the BROWSER'S midnight, and this
 * dialog posted that `Date` straight through, so axios serialised it with
 * `toISOString()`. For a US clinic that is `T04:00Z`/`T05:00Z`/`T07:00Z` — the
 * same UTC day, benign today — but it is a FOURTH anchor for
 * `Medication.startDate` / `endDate` / `expirationDate` (the column already
 * carries UTC midnight from barn ops and an instant from SOAP auto-prescribe),
 * and for any positive-offset user it stores the PREVIOUS day outright, where no
 * render fix can recover it. `datePickerToCalendarValue` reads the LOCAL y/m/d
 * the vet actually picked and anchors it at noon UTC — 12 h of slack either way,
 * the same value the invoice form and `anchorCalendarDayUtc` already write.
 *
 * `startDate` is required by the API, so a cleared picker falls back to the
 * viewer's own day (`todayLocalISO`, never UTC "today") anchored identically —
 * not to `new Date()`, which would push an instant into a calendar column. The
 * server derives the default Rx expiry from `startDate + 12 months`, so it
 * inherits this anchor too.
 */
export function toMedicationPayload(
  row: MedicationRow,
  dispenseLocationId?: string,
): MedicationRowPayload {
  const { startDate, endDate, expirationDate, ...rest } = row;
  return {
    ...rest,
    startDate: datePickerToCalendarValue(startDate) ?? ymdToNoonUtcIso(todayLocalISO()),
    endDate: datePickerToCalendarValue(endDate),
    expirationDate: datePickerToCalendarValue(expirationDate),
    // '' = Automatic — omit the field so the server infers (ADR-0013).
    dispenseLocationId: dispenseLocationId || undefined,
  };
}

interface AutocompleteOption {
  name: string;
  price?: number;
  group: 'priced' | 'terminology';
  activeIngredients?: string[];
}

interface MedicationDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * Persists the rows. Returns the created Medication records in the SAME
   * ORDER as the rows that were filled in (rows with empty name are skipped
   * in handleSave and not represented in the returned array). Returning
   * `void` is supported for back-compat with callers that don't need to
   * surface the new records — those callers can't use the dispense/print flow.
   */
  onSave: (medications: MedicationRowPayload[]) => Promise<Medication[] | void>;
  lowStockItems?: string[];
  showAll?: boolean;
  catalogItems?: CatalogItem[];
}

const MAX_ROWS = 10;
const MAX_LABELS = 5;

const MedicationDialog: React.FC<MedicationDialogProps> = ({
  open, onClose, onSave, lowStockItems = [], showAll = true, catalogItems = [],
}) => {
  const queryClient = useQueryClient();
  const [medications, setMedications] = useState<JoinedMed[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Single supported label is the DYMO 30252 Address — hardcoded; the user
  // picks their printer in the OS print dialog when "Save & Print" runs.
  const labelSize = 'DYMO_30252' as const;

  const blankRow = (): MedicationRow => ({
    name: '',
    dosage: '',
    quantity: 1,
    instructions: '',
    startDate: new Date(),
    endDate: null,
    unitPrice: undefined,
    isRx: false,
    refillsAuthorized: 0,
    expirationDate: null,
    labelCount: 1,
    deaSchedule: '',
  });

  // Default to a single row. Vets typically write one Rx at a time; the
  // "+ Add another medication" button below adds more when needed.
  const [rows, setRows] = useState<MedicationRow[]>([blankRow()]);
  // One job-level "Deduct stock from" for the whole save ('' = Automatic).
  const [dispenseLocationId, setDispenseLocationId] = useState<string>('');
  // Per-row "show advanced fields" toggle. Default off — End Date and the
  // Expires date picker are rarely changed, so they live behind a disclosure.
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});

  const toggleRowExpanded = (idx: number) =>
    setExpandedRows((prev) => ({ ...prev, [idx]: !prev[idx] }));

  useEffect(() => {
    if (!open) return;
    if (!showAll) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    fetch('/terminology/medications.equine.json')
      .then(res => {
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        return res.json();
      })
      .then((data: JoinedMed[]) => {
        setMedications(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load medications:', err);
        setError('Failed to load medication list');
        setLoading(false);
      });
  }, [open, showAll]);

  // Functional updater is required: the Drug Name Autocomplete updates two
  // fields back-to-back (name + unitPrice). With a closure-captured `rows`,
  // the second call would read stale state and clobber the first — dropping
  // the drug name. `setRows(prev => …)` makes the queued updates compose.
  const handleRowChange = (index: number, field: keyof MedicationRow, value: any) => {
    setRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleSave = async () => {
    // A named row with an empty quantity box would be dropped by the filter below without a word.
    const missingQuantity = rows.find(r => r.name && !(r.quantity > 0));
    if (missingQuantity) {
      setError(`Enter a quantity for ${missingQuantity.name}.`);
      return;
    }
    setSubmitting(true);
    setError(null);

    const filledRows = rows
      .filter(r => r.name && r.quantity > 0)
      // Anchor the three calendar pickers at noon UTC and stamp the job-level
      // deduction location on every row (see toMedicationPayload).
      .map(r => toMedicationPayload(r, dispenseLocationId));

    if (filledRows.length === 0) {
      setError('Please enter at least one medication');
      setSubmitting(false);
      return;
    }

    try {
      const created = await onSave(filledRows);

      // For each take-home Rx row with labelCount > 0: dispense the initial
      // fill (this records the fill, decrements inventory, and auto-bills the
      // visit on the backend) and collect its label data. labelCount === 0
      // means "record the Rx only" — no dispense, no bill, no print.
      const labelsToPrint: Array<{ data: RxLabelSampleData; copies: number }> = [];
      // Set by any dispense that actually committed — see the invalidation below.
      let dispensedAny = false;

      if (Array.isArray(created) && !skipWhenOffline()) {
        // Offline: the Rx record is queued and will sync, but the initial fill
        // (records the dispense, decrements inventory, auto-bills) is an
        // online-only action by design. Don't fire a doomed network call — tell
        // the vet to record the fill + print labels once back online.
        if (filledRows.some((r) => r.isRx && r.labelCount > 0)) {
          setError('Saved offline. Record the initial fill and print labels when you reconnect.');
        }
      } else if (Array.isArray(created)) {
        for (let i = 0; i < filledRows.length; i++) {
          const row = filledRows[i];
          if (!row.isRx || row.labelCount <= 0) continue;
          const med = created[i];
          if (!med) continue;
          try {
            const { event, medication } = await medicationService.dispense(med.id, {
              quantity: row.quantity,
              unit: 'units',
              labelSize,
              dispenseLocationId: dispenseLocationId || undefined,
              deaLotId: row.deaLotId,
            });
            dispensedAny = true;
            labelsToPrint.push({
              data: buildLabelData({ size: labelSize, medication, dispenseEvent: event }),
              copies: row.labelCount,
            });
          } catch (e: any) {
            // Surface the error but don't roll back the saved Medication —
            // the vet can still dispense it manually from the Refill button.
            console.error(`Initial fill dispense failed for ${row.name}:`, e);
            setError(
              `Saved Rx but failed to dispense the initial fill for ${row.name}: ` +
              (e?.response?.data?.message || e?.message || 'unknown error'),
            );
          }
        }
      }

      // The initial fill auto-bills on the backend (appendDispenseToTodayInvoice:
      // it appends to today's open visit invoice, else to today's pending invoice
      // for the horse, else creates one). The chart's Invoices tab reads
      // ['horse-invoices', horseId] and its badge reads ['horse-invoice-stats',
      // horseId] — both fetched on mount with a 60s staleTime — so without this the
      // new invoice is invisible until a reload, and the vet bills it a SECOND time
      // by hand. Prefix keys, matching MedicationCard.handleDispensed (the refill
      // path, which has always invalidated these): in React Query v3
      // ['horse-invoices'] matches ['horse-invoices', horseId].
      //
      // ⚠ This MUST stay after the dispense loop. The parent's onSave handler
      // (handleMedicationSave) runs BEFORE the loop, so an invalidation placed
      // there would refetch the invoice list before the invoice exists.
      if (dispensedAny) {
        queryClient.invalidateQueries(['horse-invoice-stats']);
        queryClient.invalidateQueries(['horse-invoices']);
      }

      // One iframe, one print dialog — all labels for this save in a single
      // job, each label its own page (no blank sheets).
      if (labelsToPrint.length > 0) {
        void printRxLabels({ size: labelSize, labels: labelsToPrint });
      }

      handleClose();
    } catch (err: any) {
      console.error('Failed to save medications:', err);
      setError(err?.response?.data?.message || 'Failed to save medications');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setRows([blankRow()]);
      setExpandedRows({});
      setError(null);
      onClose();
    }
  };

  const autocompleteOptions = useMemo((): AutocompleteOption[] => {
    const pricedOptions: AutocompleteOption[] = catalogItems
      .filter(item => item.name)
      .map(item => ({
        name: item.name,
        price: item.basePrice,
        group: 'priced' as const,
      }));

    if (!showAll) return pricedOptions;

    const pricedNamesLower = new Set(pricedOptions.map(o => o.name.toLowerCase()));

    const uniqueTradeNames = Array.from(
      new Set(medications.map(m => m.trade_name).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b));

    const terminologyOptions: AutocompleteOption[] = uniqueTradeNames
      .filter(name => !pricedNamesLower.has(name.toLowerCase()))
      .map(name => {
        const hits = medications.filter(m => m.trade_name === name);
        const ais = new Set<string>();
        hits.forEach(h => (h.active_ingredients || []).forEach(ai => ais.add(ai)));
        return {
          name,
          group: 'terminology' as const,
          activeIngredients: Array.from(ais),
        };
      });

    return [...pricedOptions, ...terminologyOptions];
  }, [catalogItems, medications, showAll]);

  const getAIs = (tradeName: string) => {
    const hits = medications.filter(m => m.trade_name === tradeName);
    const set = new Set<string>();
    hits.forEach(h => (h.active_ingredients || []).forEach(ai => set.add(ai)));
    return Array.from(set);
  };

  // Whether the Save action will also fire a print job.
  const willPrint = rows.some((r) => r.isRx && r.labelCount > 0);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Add Medication</DialogTitle>
      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {rows.some(r => r.name && lowStockItems.includes(r.name)) && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <WarningIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
            Warning: Some selected medications are low in stock or need to be purchased.
          </Alert>
        )}

        {!loading && (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ mt: 2 }}>
              {rows.map((row, idx) => (
                <Box key={idx} sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                    <Typography variant="subtitle2">Medication {idx + 1}</Typography>
                    {row.name && lowStockItems.includes(row.name) && (
                      <Chip
                        icon={<WarningIcon />}
                        label="Low Stock"
                        color="warning"
                        size="small"
                      />
                    )}
                  </Box>

                  {/* ── Type selector ──────────────────────────────────────
                   * Always-visible segmented control — no checkbox to hunt
                   * for. "Take-home Rx" reveals refills + the label stepper. */}
                  <ToggleButtonGroup
                    value={row.isRx ? 'rx' : 'inclinic'}
                    exclusive
                    size="small"
                    fullWidth
                    onChange={(_, val) => {
                      if (val) handleRowChange(idx, 'isRx', val === 'rx');
                    }}
                    sx={{ mb: 2 }}
                  >
                    <ToggleButton value="inclinic" sx={{ textTransform: 'none' }}>
                      In-clinic medication
                    </ToggleButton>
                    <ToggleButton value="rx" sx={{ textTransform: 'none' }}>
                      <LocalPharmacyIcon fontSize="small" sx={{ mr: 0.75 }} />
                      Take-home Rx
                    </ToggleButton>
                  </ToggleButtonGroup>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Autocomplete
                        options={autocompleteOptions}
                        value={autocompleteOptions.find(o => o.name === row.name) || null}
                        onChange={(_, newValue) => {
                          const name = typeof newValue === 'string' ? newValue : newValue?.name || '';
                          const price = typeof newValue === 'string' ? undefined : newValue?.price;
                          handleRowChange(idx, 'name', name);
                          handleRowChange(idx, 'unitPrice', price);
                        }}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') return option;
                          return option.price != null
                            ? `${option.name} — $${option.price.toFixed(2)}`
                            : option.name;
                        }}
                        groupBy={showAll ? (option) => option.group === 'priced' ? 'Your Priced Items' : 'Medical Database' : undefined}
                        renderOption={(props, option) => {
                          const ais = option.activeIngredients || (showAll ? getAIs(option.name) : []);
                          return (
                            <li {...props} key={`${option.name}-${option.group}`}>
                              <Box display="flex" flexDirection="column">
                                <Typography variant="body2" fontWeight={600}>
                                  {option.name}
                                  {option.price != null && (
                                    <Typography component="span" variant="body2" color="success.main" sx={{ ml: 1 }}>
                                      ${option.price.toFixed(2)}
                                    </Typography>
                                  )}
                                </Typography>
                                {ais.length > 0 && (
                                  <Typography variant="caption" color="text.secondary">
                                    {ais.join(' + ')}
                                  </Typography>
                                )}
                              </Box>
                            </li>
                          );
                        }}
                        renderInput={params => (
                          <TextField {...params} label="Drug Name" size="small" required />
                        )}
                        filterOptions={(options, { inputValue }) => {
                          const lower = inputValue.toLowerCase();
                          return options.filter(o => o.name.toLowerCase().includes(lower));
                        }}
                        isOptionEqualToValue={(option, value) => option.name === value.name}
                        freeSolo
                        onInputChange={(_, value, reason) => {
                          if (reason === 'input') {
                            handleRowChange(idx, 'name', value);
                            // Free-typed text isn't a priced catalog match.
                            handleRowChange(idx, 'unitPrice', undefined);
                          }
                        }}
                        inputValue={row.name}
                      />
                      {row.name && showAll && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          {(() => {
                            const ais = getAIs(row.name);
                            return ais.length ? `Active ingredient(s): ${ais.join(' + ')}` : '';
                          })()}
                        </Typography>
                      )}
                    </Grid>

                    <Grid item xs={12} sm={3}>
                      <TextField
                        label="Dosage"
                        size="small"
                        fullWidth
                        value={row.dosage}
                        onChange={e => handleRowChange(idx, 'dosage', e.target.value)}
                        placeholder="50mg, 10ml, etc."
                      />
                    </Grid>

                    <Grid item xs={12} sm={3}>
                      {/* Hundredths (ADR-0038): 0.53 mL bills 0.53, not 1. */}
                      <QuantityField
                        label="Quantity"
                        size="small"
                        fullWidth
                        value={row.quantity}
                        onChange={next => handleRowChange(idx, 'quantity', next ?? 0)}
                        required
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label="Instructions"
                        size="small"
                        fullWidth
                        value={row.instructions}
                        onChange={e => handleRowChange(idx, 'instructions', e.target.value)}
                        placeholder="e.g., Give 1 tablet BID PO for 7 days"
                        multiline
                      />
                    </Grid>

                    <Grid item xs={12} sm={row.isRx ? 6 : 12}>
                      {/* Start / End / Expires are CALENDAR pickers: the value
                        * kept in state is the picker's browser-local-midnight
                        * Date, and it is anchored to noon UTC at save time by
                        * toMedicationPayload — never posted raw. A picker paired
                        * with a TimePicker would be an INSTANT and would post
                        * `toISOString()` unchanged; none of these three is. */}
                      <DatePicker
                        label="Start Date"
                        value={row.startDate}
                        onChange={date => handleRowChange(idx, 'startDate', date)}
                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                      />
                    </Grid>

                    {/* ── Rx-only controls: refills + label stepper, inline ──
                     * The whole take-home Rx workflow lives in two adjacent
                     * controls — no checkbox, no separate print strip. */}
                    {row.isRx && (
                      <Grid item xs={12} sm={6}>
                        <FormControl size="small" fullWidth>
                          <InputLabel id={`refills-label-${idx}`}>Refills authorized</InputLabel>
                          <Select
                            labelId={`refills-label-${idx}`}
                            label="Refills authorized"
                            value={row.refillsAuthorized}
                            onChange={(e) => handleRowChange(idx, 'refillsAuthorized', Number(e.target.value))}
                            disabled={row.deaSchedule === 'II'}
                          >
                            <MenuItem value={0}>0 — initial fill only</MenuItem>
                            <MenuItem value={1}>1 refill (2 fills total)</MenuItem>
                            <MenuItem value={2}>2 refills (3 fills total)</MenuItem>
                            <MenuItem value={3}>3 refills (4 fills total)</MenuItem>
                            <MenuItem value={4}>4 refills (5 fills total)</MenuItem>
                            <MenuItem value={5}>5 refills (6 fills total)</MenuItem>
                          </Select>
                          {row.deaSchedule === 'II' && (
                            <FormHelperText>
                              Schedule II — refills not permitted; each fill needs a new Rx
                            </FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                    )}

                    {row.isRx && (
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            flexWrap: 'wrap',
                            p: 1.25,
                            borderRadius: 1,
                            bgcolor: 'grey.50',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PrintIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              Labels to print
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              bgcolor: 'background.paper',
                            }}
                          >
                            <IconButton
                              size="small"
                              aria-label="Fewer labels"
                              disabled={row.labelCount <= 0}
                              onClick={() => handleRowChange(idx, 'labelCount', Math.max(0, row.labelCount - 1))}
                            >
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                            <Typography sx={{ minWidth: 32, textAlign: 'center', fontWeight: 700 }}>
                              {row.labelCount}
                            </Typography>
                            <IconButton
                              size="small"
                              aria-label="More labels"
                              disabled={row.labelCount >= MAX_LABELS}
                              onClick={() => handleRowChange(idx, 'labelCount', Math.min(MAX_LABELS, row.labelCount + 1))}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {row.labelCount === 0
                              ? 'Records the Rx only — no fill or label printed now.'
                              : `Dispenses the initial fill and prints ${row.labelCount} label${row.labelCount > 1 ? 's' : ''}.`}
                          </Typography>
                        </Box>
                      </Grid>
                    )}

                    {/* ── Advanced options disclosure ─────────────────────── */}
                    <Grid item xs={12}>
                      <Button
                        size="small"
                        onClick={() => toggleRowExpanded(idx)}
                        sx={{
                          textTransform: 'none',
                          color: 'text.secondary',
                          fontSize: '0.75rem',
                          mt: -0.5,
                        }}
                      >
                        {expandedRows[idx]
                          ? '− Hide advanced options'
                          : `+ More options (end date${row.isRx ? ', expiration override' : ''}, DEA schedule)`}
                      </Button>
                    </Grid>

                    {expandedRows[idx] && (
                      <Grid item xs={12} sm={6}>
                        <DatePicker
                          label="End Date"
                          value={row.endDate}
                          onChange={date => handleRowChange(idx, 'endDate', date)}
                          slotProps={{ textField: { size: 'small', fullWidth: true } }}
                        />
                      </Grid>
                    )}

                    {row.isRx && expandedRows[idx] && (
                      <Grid item xs={12} sm={6}>
                        <DatePicker
                          label="Expires"
                          value={row.expirationDate}
                          onChange={(date) => handleRowChange(idx, 'expirationDate', date)}
                          slotProps={{
                            textField: {
                              size: 'small',
                              fullWidth: true,
                              helperText: 'Defaults to Start + 12 months',
                            },
                          }}
                        />
                      </Grid>
                    )}

                    {/* DEA controlled-substance schedule. Applies to in-clinic
                     * meds and take-home Rx alike (controlled substances are
                     * logged either way); for Rx, picking C-II also zeroes the
                     * refills picker above. */}
                    {expandedRows[idx] && (
                      <Grid item xs={12} sm={6}>
                        <FormControl size="small" fullWidth>
                          <InputLabel id={`dea-label-${idx}`}>DEA Schedule</InputLabel>
                          <Select
                            labelId={`dea-label-${idx}`}
                            label="DEA Schedule"
                            value={row.deaSchedule}
                            onChange={(e) => {
                              const val = String(e.target.value);
                              handleRowChange(idx, 'deaSchedule', val);
                              // Schedule II can't be refilled — force refills to 0.
                              if (val === 'II') handleRowChange(idx, 'refillsAuthorized', 0);
                            }}
                          >
                            <MenuItem value="">Not controlled</MenuItem>
                            <MenuItem value="II">Schedule II</MenuItem>
                            <MenuItem value="III">Schedule III</MenuItem>
                            <MenuItem value="IV">Schedule IV</MenuItem>
                            <MenuItem value="V">Schedule V</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                    {/* DEA bottle prompt — renders only when this row's
                     * drug single-matches the clinic's CS registry (ADR-0014);
                     * preselects the FIFO bottle so an untouched control still
                     * stamps the bottle the user saw. */}
                    <Grid item xs={12}>
                      <DeaBottleSelect
                        drugName={row.name}
                        declaredSchedule={row.deaSchedule || undefined}
                        value={row.deaLotId ?? ''}
                        onChange={(lotId) => handleRowChange(idx, 'deaLotId', lotId || undefined)}
                        quantity={row.quantity || undefined}
                        disabled={submitting}
                        margin="none"
                      />
                    </Grid>
                  </Grid>
                </Box>
              ))}

              <Box textAlign="center">
                <Button
                  variant="outlined"
                  onClick={() => setRows(prev => (prev.length < MAX_ROWS ? [...prev, blankRow()] : prev))}
                  size="small"
                  disabled={rows.length >= MAX_ROWS}
                >
                  {rows.length >= MAX_ROWS ? 'Maximum Reached' : '+ Add another medication'}
                </Button>
              </Box>

              {/* One job-level deduction source for every row saved here.
                  Renders only when the clinic has trucks (ADR-0013). */}
              <DeductFromLocationSelect
                value={dispenseLocationId}
                onChange={setDispenseLocationId}
                disabled={submitting}
              />

            </Box>
          </LocalizationProvider>
        )}
      </DialogContent>

      {/* ── Label size — one job-level setting, shown only when something
       * will actually print. Not a checkbox, not a clutter strip — a single
       * contextual control. ───────────────────────────────────────────── */}
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={submitting || loading}>
          {submitting
            ? 'Saving...'
            : willPrint
              ? 'Save & Print'
              : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MedicationDialog;
